// Thẻ thông báo ở cột trái, 2 tab: Công ty (bảng tin, xác nhận đã đọc) và Của tôi (thông báo cá nhân từ API)
import React, { useMemo, useState } from 'react';
import { Button, Modal, Tag, message } from 'antd';
import dayjs from 'dayjs';
import { ANN_LEVEL, ROLE_META, visibleAnnouncements, type Announcement, type AnnLevel, type HomeRole } from './homeData';
import useHomeStore from './store';
import { Card, DemoChip } from './ui';
import { MyNotificationsList, useMyNotifications } from './MyNotifications';

const ago = (iso: string) => {
  const m = dayjs().diff(dayjs(iso), 'minute');
  if (m < 60) return `${Math.max(1, m)} phút`;
  if (m < 24 * 60) return `${Math.floor(m / 60)} giờ`;
  if (m < 7 * 24 * 60) return `${Math.floor(m / 1440)} ngày`;
  return dayjs(iso).format('DD/MM/YYYY');
};

const LevelTag = ({ level }: { level: AnnLevel }) => (
  <span className={`shrink-0 rounded border px-1.5 py-px text-[10px] font-semibold ${ANN_LEVEL[level].cls}`}>{ANN_LEVEL[level].label}</span>
);

interface Me { key: string; name: string }

const LEVEL_BAR: Record<AnnLevel, string> = { urgent: '#fb7185', policy: '#fbbf24', event: '#38bdf8', info: '#94a3b8' };

/** Thẻ thông báo: tab Công ty (tin quan trọng nhất nổi bật, các tin khác dạng dòng) và tab Của tôi */
export function AnnouncementPanel({ role, me, recipientId, canManage }: {
  role: HomeRole; me: Me; recipientId: number | null; canManage: boolean;
}) {
  const mine = useMyNotifications(recipientId);
  const [tab, setTab] = useState<'company' | 'mine' | null>(null);
  const all = useHomeStore(s => s.announcements);
  const acks = useHomeStore(s => s.acks);
  const ack = useHomeStore(s => s.ack);
  const [openId, setOpenId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const list = useMemo(() => visibleAnnouncements(all, role), [all, role]);
  const isRead = (a: Announcement) => !!acks[a.id]?.[me.key];
  const unread = list.filter(a => !isRead(a)).length;
  const featured = list.find(a => !isRead(a) && (a.requireAck || a.level === 'urgent'))
    || list.find(a => !isRead(a) && a.pinned) || list.find(a => !isRead(a)) || list[0];
  const rest = list.filter(a => a !== featured);
  const shown = showAll ? rest : rest.slice(0, 3);
  // Mặc định mở tab đang có việc: tin công ty cần xác nhận / chưa đọc, nếu không thì thông báo cá nhân chưa đọc
  const mustAck = list.some(a => a.requireAck && !isRead(a));
  const active = tab || (mustAck || unread || !mine.unread ? 'company' : 'mine');
  const tabBtn = (key: 'company' | 'mine', label: string, count: number) => (
    <button onClick={() => setTab(key)}
      className={`relative flex items-center gap-1.5 whitespace-nowrap pb-2 pt-0.5 text-[13px] font-semibold transition ${active === key ? 'text-white' : 'text-slate-500 hover:text-slate-300'}`}>
      {label}
      {count > 0 && <span className={`rounded-full px-1.5 text-[10px] leading-4 tabular-nums ${active === key ? 'bg-rose-500 text-white' : 'bg-white/10 text-slate-300'}`}>{count}</span>}
      {active === key && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-teal-400" />}
    </button>
  );

  return (
    <>
      <Card>
        <div className="flex items-end gap-4 border-b border-white/[0.06] px-4 pt-3">
          <i className="fa-regular fa-bell mb-2.5 -mr-2 text-[13px] text-slate-400" />
          {tabBtn('company', 'Công ty', unread)}
          {tabBtn('mine', 'Của tôi', mine.unread)}
          <span className="flex-1" />
          <div className="pb-1.5">
            {active === 'mine' && mine.demo && <DemoChip />}
          </div>
        </div>
        {active === 'mine' ? <div className="pt-1.5"><MyNotificationsList st={mine} /></div> : (
        <div className="px-3 pb-3 pt-3">
          {!featured ? (
            <div className="flex flex-col items-center gap-1 py-6 text-center text-xs text-slate-500">
              <i className="fa-regular fa-newspaper text-2xl text-slate-600" />Chưa có tin mới
            </div>
          ) : (
            <>
              <article className="rounded-xl border border-white/[0.06] border-l-[3px] bg-white/[0.04] p-3" style={{ borderLeftColor: LEVEL_BAR[featured.level] }}>
                <div className="mb-1.5 flex items-center gap-1.5 text-[11px] text-slate-400">
                  <LevelTag level={featured.level} />
                  {featured.pinned && <i className="fa-solid fa-thumbtack text-[10px] text-amber-300" title="Ghim" />}
                  <span className="ml-auto">{ago(featured.createdAt)}</span>
                </div>
                <button onClick={() => setOpenId(featured.id)} className="block text-left text-[14px] font-semibold leading-snug text-white hover:underline">
                  {featured.title}
                </button>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-400">{featured.content}</p>
                <div className="mt-2.5 flex items-center gap-2">
                  {featured.requireAck && !isRead(featured) ? (
                    <button onClick={() => { ack(featured.id, me.key, me.name); message.success('Đã xác nhận đọc'); }}
                      className="rounded-lg bg-teal-500 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-teal-400">
                      <i className="fa-solid fa-check mr-1" />Đã đọc và hiểu
                    </button>
                  ) : isRead(featured) && (
                    <span className="text-xs text-emerald-300/80"><i className="fa-solid fa-check-double mr-1" />Đã đọc</span>
                  )}
                  <button onClick={() => setOpenId(featured.id)} className="ml-auto text-xs font-medium text-slate-300 hover:text-white">
                    Xem chi tiết <i className="fa-solid fa-arrow-right ml-0.5 text-[10px]" />
                  </button>
                </div>
              </article>

              {shown.length > 0 && (
                <div className="mt-2">
                  {shown.map(a => (
                    <button key={a.id} onClick={() => setOpenId(a.id)} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition hover:bg-white/[0.05]">
                      <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: isRead(a) ? 'transparent' : LEVEL_BAR[a.level], boxShadow: isRead(a) ? 'inset 0 0 0 1px rgba(148,163,184,.4)' : undefined }} />
                      <span className={`min-w-0 flex-1 truncate text-[13px] ${isRead(a) ? 'text-slate-400' : 'font-medium text-slate-100'}`}>{a.title}</span>
                      {a.requireAck && !isRead(a) && <i className="fa-solid fa-circle-exclamation text-[11px] text-rose-300" title="Cần xác nhận đã đọc" />}
                      <span className="shrink-0 text-[11px] text-slate-500">{ago(a.createdAt)}</span>
                    </button>
                  ))}
                </div>
              )}
              {rest.length > 3 && (
                <button onClick={() => setShowAll(v => !v)} className="mt-1 w-full rounded-lg py-1.5 text-xs font-medium text-slate-400 transition hover:bg-white/[0.05] hover:text-white">
                  {showAll ? 'Thu gọn' : `Xem thêm ${rest.length - 3} tin`}
                </button>
              )}
            </>
          )}
        </div>
        )}
      </Card>
      <AnnouncementDetail id={openId} me={me} canManage={canManage} onClose={() => setOpenId(null)} />
    </>
  );
}

function AnnouncementDetail({ id, me, canManage, onClose }: { id: string | null; me: Me; canManage: boolean; onClose: () => void }) {
  const a = useHomeStore(s => s.announcements.find(x => x.id === id));
  const acks = useHomeStore(s => (id ? s.acks[id] : undefined));
  const ack = useHomeStore(s => s.ack);
  if (!a) return null;
  const read = !!acks?.[me.key];
  const readers = Object.values(acks || {}).sort((x, y) => y.at.localeCompare(x.at));
  const close = () => { if (!a.requireAck && !read) ack(a.id, me.key, me.name); onClose(); };

  return (
    <Modal
      open={!!id} onCancel={close} title={<span className="flex items-center gap-2"><Tag className="!m-0">{ANN_LEVEL[a.level].label}</Tag>{a.title}</span>}
      footer={a.requireAck && !read
        ? <Button type="primary" onClick={() => { ack(a.id, me.key, me.name); message.success('Đã xác nhận đọc'); onClose(); }}>Tôi đã đọc và hiểu</Button>
        : <Button onClick={close}>Đóng</Button>}
    >
      <div className="mb-2 text-xs text-slate-500">
        {a.by} · {dayjs(a.createdAt).format('HH:mm DD/MM/YYYY')}
        {' · '}{a.audience.length ? a.audience.map(r => ROLE_META[r].label).join(', ') : 'Toàn công ty'}
        {a.until && ` · hiển thị đến ${dayjs(a.until).format('DD/MM')}`}
      </div>
      <div className="whitespace-pre-line text-sm text-slate-800">{a.content}</div>
      {read && <div className="mt-3 text-xs text-emerald-700"><i className="fa-solid fa-check mr-1" />Bạn đã xác nhận lúc {dayjs(acks![me.key].at).format('HH:mm DD/MM')}</div>}
      {canManage && (
        <div className="mt-4 rounded-lg bg-slate-50 p-2.5 text-xs">
          <b>{readers.length} người đã {a.requireAck ? 'xác nhận' : 'xem'}</b>
          {readers.length > 0 && <div className="mt-1 text-slate-600">{readers.slice(0, 20).map(r => r.name).join(', ')}{readers.length > 20 ? '…' : ''}</div>}
          <div className="mt-1 text-slate-400">Đang lưu trên trình duyệt này – khi có API sẽ tổng hợp từ mọi máy.</div>
        </div>
      )}
    </Modal>
  );
}
